import moment from 'moment'

// Normalize Booking API response into UI-friendly rows
export const mapBookings = (response) => {
  if (response && response.data && response.data.success === true) {
    const rows = Array.isArray(response.data.data)
      ? response.data.data.map((b) => ({
          id: b.id,
          slack: b.slack,
          event_code: b.event_code,
          event_type: b.event_type,
          start_date: b.start_date,
          end_date: b.end_date,
          name: b.name,
          email: b.email,
          phone: b.phone,
          description: b.description,
          no_of_persons: b.no_of_persons,
          created_at: b.created_at,
          updated_at: b.updated_at,
          created_by: b.created_by,
          updated_by: b.updated_by,
          store: b.store
        }))
      : []

    return {
      success: true,
      total: response.data.total || rows.length,
      data: rows
    }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch bookings',
    data: []
  }
}

// Normalize Calendar API response for FullCalendar
export const mapCalendarEvents = (response) => {
  if (response && response.data && response.data.success === true) {
    console.log('Raw calendar events from API:', response.data.data)
    const events = Array.isArray(response.data.data)
      ? response.data.data.map((e) => {
          console.log('Processing event from API:', {
            id: e.id,
            name: e.name,
            email: e.email,
            phone: e.phone,
            no_of_persons: e.no_of_persons,
            description: e.description,
            start: e.start,
            end: e.end,
            extendedProps: e.extendedProps
          })
          // Use the actual slack from the API response
          const slack = e.slack || e.id

          // Use the actual start and end times from the API
          let startDate, endDate

          // Handle timezone conversion with standard moment
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

          if (e.start && e.start.includes('T')) {
            if (e.start.includes('Z')) {
              // UTC time - keep as UTC for FullCalendar
              startDate = moment.utc(e.start).toDate()
            } else if (e.start.includes('+') || e.start.includes('-')) {
              // Time with timezone offset - parse as is (moment handles it)
              startDate = moment(e.start).toDate()
            } else {
              // Local time - parse directly
              startDate = moment(e.start).toDate()
            }
          } else if (e.start) {
            // If it's just a date without time, add default time
            startDate = moment(e.start + 'T09:00:00').toDate()
          } else {
            startDate = new Date()
          }

          if (e.end && e.end.includes('T')) {
            if (e.end.includes('Z')) {
              // UTC time - keep as UTC for FullCalendar
              endDate = moment.utc(e.end).toDate()
            } else if (e.end.includes('+') || e.end.includes('-')) {
              // Time with timezone offset - parse as is (moment handles it)
              endDate = moment(e.end).toDate()
            } else {
              // Local time - parse directly
              endDate = moment(e.end).toDate()
            }
          } else if (e.end) {
            // If it's just a date without time, add default time
            endDate = moment(e.end + 'T10:00:00').toDate()
          } else {
            endDate = new Date()
          }

          console.log('Time processing:', {
            userTimezone: userTimezone,
            originalStart: e.start,
            originalEnd: e.end,
            isStartUTC: e.start && e.start.includes('Z'),
            isEndUTC: e.end && e.end.includes('Z'),
            processedStart: startDate.toISOString(),
            processedEnd: endDate.toISOString(),
            startUTC: moment.utc(startDate).format('YYYY-MM-DD HH:mm:ss'),
            endUTC: moment.utc(endDate).format('YYYY-MM-DD HH:mm:ss'),
            startLocal: moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
            endLocal: moment(endDate).format('YYYY-MM-DD HH:mm:ss')
          })

          const event = {
            id: e.id,
            slack: slack, // Use actual slack for routing
            title:
              e.name || `${e.event_type || 'Booking'} ${e.event_code || e.id}`, // Always use name as title, fallback to event code
            start: startDate.toISOString(), // Convert back to ISO string for FullCalendar
            end: endDate.toISOString(), // Convert back to ISO string for FullCalendar
            allDay: false, // Ensure events show in all views
            extendedProps: {
              slack: slack,
              id: e.id, // Include id for tooltip
              // Include all booking details for tooltip - prioritize main object data over extendedProps
              event_code: e.event_code,
              event_type: e.event_type,
              start_date: e.start, // Use original start from API
              end_date: e.end, // Use original end from API
              name: e.name,
              email: e.email,
              phone: e.phone,
              description: e.description,
              no_of_persons: e.no_of_persons,
              created_at: e.created_at,
              updated_at: e.updated_at,
              created_by: e.created_by,
              updated_by: e.updated_by,
              store: e.store,
              // Include any additional extendedProps that might not be in main object
              ...e.extendedProps
            },
            color: e.color || '#62C163'
          }

          console.log('Processed event:', {
            original: {
              start: e.start,
              end: e.end,
              title: e.title,
              name: e.name,
              email: e.email,
              phone: e.phone,
              no_of_persons: e.no_of_persons,
              description: e.description
            },
            processed: {
              start: event.start,
              end: event.end,
              title: event.title
            },
            startDate: startDate,
            endDate: endDate,
            startDateString: startDate.toISOString(),
            endDateString: endDate.toISOString(),
            startDateLocal: moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
            endDateLocal: moment(endDate).format('YYYY-MM-DD HH:mm:ss'),
            startDateForCalendar: moment(startDate).format(
              'YYYY-MM-DDTHH:mm:ss'
            ),
            endDateForCalendar: moment(endDate).format('YYYY-MM-DDTHH:mm:ss'),
            title: event.title,
            extendedProps: event.extendedProps,
            extendedPropsDetails: {
              name: event.extendedProps.name,
              email: event.extendedProps.email,
              phone: event.extendedProps.phone,
              no_of_persons: event.extendedProps.no_of_persons,
              description: event.extendedProps.description
            }
          })

          // Validate event data
          if (!event.start || !event.end) {
            console.error('Invalid event data:', event)
          }

          // Check if end time is exactly on the hour
          const endTime = moment(endDate)
          const isExactHour = endTime.minute() === 0 && endTime.second() === 0
          console.log('End time analysis:', {
            endTime: endTime.format('YYYY-MM-DD HH:mm:ss'),
            isExactHour: isExactHour,
            minute: endTime.minute(),
            second: endTime.second(),
            originalEnd: e.end,
            processedEnd: endDate.toISOString()
          })

          // If the end time should be exactly on the hour, ensure it is
          if (e.end && e.end.includes('T') && e.end.includes('Z')) {
            const originalEnd = moment(e.end)
            if (originalEnd.minute() === 0 && originalEnd.second() === 0) {
              // Ensure the end time is exactly on the hour
              endDate = originalEnd.toDate()
              console.log(
                'Adjusted end time to exact hour:',
                endDate.toISOString()
              )
            }
          }

          return event
        })
      : []
    return { success: true, data: events }
  }
  return {
    success: false,
    message: response?.data?.message || 'Failed to fetch calendar events',
    data: []
  }
}
